/**
 * Connection API Service
 *
 * Azure Management API calls for connection CRUD operations.
 * Based on LogicAppsUX patterns from designer-client-services.
 */

import type {
  ConnectionReference,
  ConnectionStatus,
  AvailableConnection,
} from '@/types/connectionReference';

import { getUniqueName } from './manifestConnectionService';
import { apiVersions, getLogicEndpoint } from './endpoints';
import { extractConnectorName, isManagedApiConnector, toShortManagedApiId } from '@/utils/connections';
import { parseJsonBody } from './apiError';
import { buildRuntimeUrl } from './acaAppUrl';


// =============================================================================
// TYPES
// =============================================================================

/**
 * Azure Resource location for connection operations
 */
export interface AzureResourceContext {
  subscriptionId: string;
  resourceGroup: string;
  location: string;
  accessToken: string;
}

export interface AppResourceContext {
  accessToken: string;
  projectName: string;
  appId: string;
  location: string;
}

/**
 * Parameters for creating a new connection
 */
export interface CreateConnectionParams {
  /** Display name for the connection */
  connectionName: string;
  /** Connector/API resource ID */
  connectorId: string;
  /** Parameter values for the connection */
  parameterValues: Record<string, unknown>;
  /** Non-secret connection parameters persisted on the connection resource */
  connectionParameters?: Record<string, unknown>;
  /** Connection parameter set name (for multi-auth connectors) */
  parameterSetName?: string;
  /** Whether this is a first-party OAuth connection */
  isFirstPartyConnection?: boolean;
  /** Sync connection after successful connection creation
   * Default is true, should be set to false for OAuth connection since consent flow needs to be completed.
   */
  syncAfterCreation?: boolean; 
}

/**
 * Parameters for updating an existing connection
 */
export interface UpdateConnectionParams {
  /** Connection resource ID */
  connectionId: string;
  /** Updated parameter values (non-secret only) */
  parameterValues?: Record<string, unknown>;
  /** Updated display name */
  displayName?: string;
  /** Parameter set name (auth type) - when changing authentication method */
  parameterSetName?: string;
}

/**
 * Result of a connection test operation
 */
export interface TestConnectionResult {
  /** Whether the test was successful */
  success: boolean;
  /** Connection status after test */
  status: ConnectionStatus;
  /** Human-readable message */
  message?: string;
  /** Detailed error information */
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/**
 * Connection resource from Azure Management API
 */
export interface ConnectionResource {
  id: string;
  name: string;
  type: string;
  location: string;
  properties: {
    api?: {
      id: string;
      name: string;
      displayName?: string;
      iconUri?: string;
      brandColor?: string;
    };
    displayName?: string;
    statuses?: Array<{
      status: string;
      error?: {
        code: string;
        message: string;
      };
    }>;
    customParameterValues?: Record<string, unknown>;
    parameterValueSet?: {
      name: string;
      values: Record<string, unknown>;
    };
    /** Single-auth MI marker (Logic Apps Standard sets this to `'Alternative'`; see `isConnectionSingleAuthManagedIdentity`). */
    parameterValueType?: string;
    connectionParameters?: Record<string, unknown>;
    connectionRuntimeUrl?: string;
    createdTime?: string;
    changedTime?: string;
    testLinks?: Array<{
      requestUri: string;
      method: string;
    }>;
    testRequests?: Array<{
      uri: string;
      method: string;
    }>;
  };
}

export interface AppConnectionResource {
  id: string;
  name: string;
  type: string;
  properties: {
    changedTime: string;
    connectionId: string;
    connectionRuntimeUrl: string;
    connectorId: string;
    createdBy: {
      objectId: string;
      tenantId: string;
    };
    createdTime: string;
    displayName: string;
    overallStatus: string;
    provisioningState: string;
    statuses?: Array<{
      status: string;
      error?: {
        code: string;
        message: string;
      };
    }>;
  }
}

/**
 * Azure API error response
 */
export interface AzureApiError {
  error: {
    code: string;
    message: string;
    details?: Array<{
      code: string;
      message: string;
    }>;
  };
}

// =============================================================================
// CONSTANTS
// =============================================================================

const OTTO_API_VERSION = apiVersions.otto;

/**
 * ARM API version for Microsoft.Web/connections — same as Logic Apps Standard.
 * Used for create, listConsentLinks, confirmConsentCode on managed connectors.
 */

const isAppUrl = (url: string): boolean => url.toLowerCase().startsWith('/automationprojects/');
const getAppBasedUrl = (ctx: Omit<AppResourceContext, 'location'>): string => {
  if (ctx.appId && isAppUrl(ctx.appId)) {
    return `${getLogicEndpoint()}${ctx.appId}/connections`;
  }

  return `${ctx.appId}/connections`;
}

/**
 * Build base URL for project-scoped source control connections.
 * Uses `/automationProjects/{project}/sourceControlConnections` (no applications segment).
 */
const getSourceControlUrl = (projectName: string): string => {
  const encodedProjectName = encodeURIComponent(projectName);
  return `${getLogicEndpoint()}/automationProjects/${encodedProjectName}/sourceControlConnections`;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Build base URL for connection resources.
 * When useSourceControl is true, uses project-scoped sourceControlConnections routes.
 */
const buildConnectionsUrl = (ctx: Omit<AppResourceContext, 'location'>, connectionName?: string, useSourceControl?: boolean): string => {
  const base = useSourceControl
    ? getSourceControlUrl(ctx.projectName)
    : getAppBasedUrl(ctx);
  return connectionName ? `${base}/${connectionName}` : base;
};

const buildConnectorMetadataUrl = (projectName: string, connectorId: string): string => {
  const normalizedConnectorId = connectorId.startsWith('/') ? connectorId : `/${connectorId}`;
  const connectorPath = isAppUrl(normalizedConnectorId)
    ? normalizedConnectorId
    : `/automationProjects/${encodeURIComponent(projectName)}${normalizedConnectorId}`;

  return `${getLogicEndpoint()}${connectorPath}?api-version=${OTTO_API_VERSION}`;
};

/**
 * Make authenticated request to Azure Management API.
 */
async function appFetch<T>(
  url: string,
  accessToken: string,
  options: RequestInit & { skipAuthorization?: boolean } = {}
): Promise<T> {
  const { skipAuthorization = false, ...requestOptions } = options;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(requestOptions.headers as Record<string, string>),
  };

  if (!skipAuthorization) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    ...requestOptions,
    headers,
  });

  if (!response.ok) {
    let errorData: AzureApiError | null = null;
    const text = await response.text();
    const parsed = parseJsonBody(text);
    if (parsed && typeof parsed === 'object') {
      errorData = parsed as AzureApiError;
    }

    const errorMessage = errorData?.error?.message ?? `Request failed: ${response.status}`;
    const error = new Error(errorMessage) as Error & { code?: string | undefined; status?: number };
    error.code = errorData?.error?.code ?? undefined;
    error.status = response.status;
    throw error;
  }

  // Handle empty responses (204 No Content, or empty body)
  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}

/**
 * Extract the short connection name from a potentially pathed resource name.
 * The Otto data-plane API returns names like "project/app/connection-name";
 * this strips the path prefix and returns only the last segment.
 */
export function extractConnectionName(name: string): string {
  if (!name) return name;
  const parts = name.split('/');
  return parts[parts.length - 1] ?? name;
}

/**
 * Derive connection status from a ConnectionResource.
 * Prefers overallStatus (authoritative for Otto data-plane responses)
 * over the properties.statuses array.
 */
function deriveConnectionStatus(connection: ConnectionResource): ConnectionStatus {
  const overallStatus = (connection as unknown as AppConnectionResource).properties?.overallStatus;
  if (overallStatus) {
    const normalized = overallStatus.toLowerCase();
    if (normalized === 'connected' || normalized === 'ready') return 'connected';
    if (normalized === 'unauthenticated' || normalized === 'notconnected') return 'unauthenticated';
    return 'error';
  }
  return mapConnectionStatus(connection.properties.statuses);
}

/**
 * Map Azure connection status to our status type
 */
function mapConnectionStatus(
  statuses?: Array<{ status: string; error?: { code: string; message: string } }>
): ConnectionStatus {
  if (!statuses || statuses.length === 0) {
    return 'connected';
  }

  const primaryStatus = statuses[0];
  if (!primaryStatus) {
    return 'connected';
  }

  const status = primaryStatus.status.toLowerCase();

  if (status === 'connected' || status === 'ready') {
    return 'connected';
  }
  if (status === 'unauthenticated' || status === 'error' && primaryStatus.error?.code === 'Unauthenticated') {
    return 'unauthenticated';
  }
  return 'error';
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Normalize a connector ID to the `/managedApis/{name}` format expected by the data-plane API.
 * Handles:
 *   - Already canonical: `/managedApis/office365` → `/managedApis/office365`
 *   - Data-plane path: `/automationProjects/.../managedApis/office365` → `/managedApis/office365`
 *   - ARM path: `/subscriptions/.../managedApis/office365` → `/managedApis/office365`
 */
function toManagedConnectorId(connectorId: string): string {
  return toShortManagedApiId(connectorId);
}

/**
 * Create a new connection resource in Automation app.
 * When useSourceControl is true, uses project-scoped sourceControlConnections routes.
 */
export async function createConnection(
  ctx: AppResourceContext,
  params: CreateConnectionParams,
  useSourceControl?: boolean,
): Promise<ConnectionResource> {
  const url = `${buildConnectionsUrl(ctx, params.connectionName, useSourceControl)}?api-version=${OTTO_API_VERSION}`;

  // For multi-auth connectors (e.g., Service Bus with ManagedIdentity / ConnectionString
  // parameter sets) the Azure managed-API schema requires `parameterValueSet: { name, values }`
  // instead of separate `parameterValues` + `parameterSetName`. Mirrors LogicAppsUX
  // `_getRequestForCreateConnection`. See connection.ts in libs/logic-apps-shared/.../base.
  const properties: Record<string, unknown> = {
    displayName: params.connectionName,
    connectorId: toManagedConnectorId(params.connectorId),
  };

  if (params.parameterSetName) {
    properties.parameterValueSet = {
      name: params.parameterSetName,
      values: Object.fromEntries(
        Object.entries(params.parameterValues ?? {})
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, { value }])
      ),
    };
  } else {
    properties.parameterValues = params.parameterValues;
  }

  const connection = await appFetch<ConnectionResource>(url, ctx.accessToken, {
    method: 'PUT',
    body: JSON.stringify({ properties }),
  });

  if (params.syncAfterCreation === false || useSourceControl) {
    // Skip sync when explicitly disabled or when using project-scoped
    // sourceControlConnections routes (no /sync endpoint exists).
    return connection;
  }

  try {
    await syncConnection(ctx, params.connectionName);
    return connection;
  } catch (error) {  // If sync fails, attempt to delete the created connection to avoid leaving broken connections
    console.warn('Connection created but failed to sync. Attempting to delete the connection to avoid leaving it in a broken state.', error);
    try {
      await deleteConnection(ctx, params.connectionName, useSourceControl);
    } catch (deleteError) {
      console.warn('Failed to delete the connection after sync failure.', deleteError);
    }
    
    throw error;
  }
}

export async function syncConnection(
  ctx: Omit<AppResourceContext, 'location'>,
  connectionName: string,
): Promise<void> {
  const url = `${buildConnectionsUrl(ctx, connectionName)}/sync?api-version=${OTTO_API_VERSION}`;
  await appFetch<void>(url, ctx.accessToken, {
    method: 'POST',
  });
}

/**
 * Delete a connection resource
 */
export async function deleteConnection(
  ctx: AppResourceContext,
  connectionName: string,
  useSourceControl?: boolean,
): Promise<void> {
  const url = `${buildConnectionsUrl(ctx, connectionName, useSourceControl)}?api-version=${OTTO_API_VERSION}`;

  await appFetch<void>(url, ctx.accessToken, {
    method: 'DELETE',
  });
}

/**
 * Check if a connection name is unique (doesn't exist) in Azure API Hub.
 * Returns true if unique (404 = not found), false if exists (200 or error).
 * 
 * This follows the pattern from LogicAppsUX:
 * - 404 = unique (name is available) ✅
 * - 200 = exists (name is taken) ❌  
 * - Other errors = assume not unique (safe default) ❌
 */
export async function isConnectionNameUnique(
  ctx: AppResourceContext,
  connectionName: string,
  useSourceControl?: boolean,
): Promise<boolean> {
  const url = `${buildConnectionsUrl(ctx, connectionName, useSourceControl)}?api-version=${OTTO_API_VERSION}`;
  try {
    await appFetch<ConnectionResource>(url, ctx.accessToken);
    return false; // 200 = exists = NOT unique
  } catch (error) {
    // Check for 404 status - this means the connection doesn't exist = IS unique
    const err = error as Error & { status?: number };
    if (err.status === 404) {
      return true; // 404 = not found = IS unique
    }
    // For other errors, assume not unique (safe default)
    return false;
  }
}

/**
 * Get a connection resource by name
 */
export async function getConnection(
  ctx: AppResourceContext,
  connectionName: string,
  useSourceControl?: boolean,
): Promise<ConnectionResource> {
  const url = `${buildConnectionsUrl(ctx, connectionName, useSourceControl)}?api-version=${OTTO_API_VERSION}`;
  return appFetch<ConnectionResource>(url, ctx.accessToken);
}

/**
 * List all connections in an application, optionally filtering by connector ID.
 * When useSourceControl is true, lists project-scoped source control connections.
 */
export async function listConnections(
  ctx: AppResourceContext,
  connectorId?: string,
  useSourceControl?: boolean,
): Promise<ConnectionResource[]> {
  let url = `${buildConnectionsUrl(ctx, undefined, useSourceControl)}?api-version=${OTTO_API_VERSION}`;

  // Filter by connector if specified
  if (connectorId) {
    const connectorName = extractConnectorName(connectorId);
    url += `&$filter=properties/api/id eq '${connectorId}' or properties/api/name eq '${connectorName}'`;
  }

  const response = await appFetch<{ value: ConnectionResource[] }>(url, ctx.accessToken);
  return response.value;
}

/**
 * Test a connection's validity
 */
export async function testConnection(
  ctx: AppResourceContext,
  connectionName: string,
  useSourceControl?: boolean,
): Promise<TestConnectionResult> {
  try {
    // First, get the connection to check its status
    const connection = await getConnection(ctx, connectionName, useSourceControl);

    // Check if connection has test links
    const testLink = connection.properties.testLinks?.[0] ?? connection.properties.testRequests?.[0];

    if (testLink) {
      // Execute the test request
      const testLinkWithUri = testLink as { requestUri?: string; uri?: string; method: string };
      const testUrl = testLinkWithUri.requestUri ?? testLinkWithUri.uri;
      const testMethod = testLink.method ?? 'GET';

      if (testUrl) {
        try {
          await appFetch<unknown>(testUrl, ctx.accessToken, {
            method: testMethod,
          });

          return {
            success: true,
            status: 'connected',
            message: 'Connection test successful',
          };
        } catch (testError) {
          const error = testError as Error & { code?: string };
          return {
            success: false,
            status: error.code === 'Unauthenticated' ? 'unauthenticated' : 'error',
            message: 'Connection test failed',
            error: {
              code: error.code ?? 'TestFailed',
              message: error.message,
            },
          };
        }
      }
    }

    // Fall back to status check (Otto automation connections don't have testLinks).
    const status = deriveConnectionStatus(connection);
    return {
      success: status === 'connected',
      status,
      message: status === 'connected' ? 'Connection is valid' : 'Connection has issues',
    };
  } catch (error) {
    const err = error as Error & { code?: string };
    return {
      success: false,
      status: 'error',
      message: 'Failed to test connection',
      error: {
        code: err.code ?? 'Unknown',
        message: err.message,
      },
    };
  }
}

/**
 * Get connection status
 */
export async function getConnectionStatus(
  ctx: AppResourceContext,
  connectionName: string,
  useSourceControl?: boolean,
): Promise<ConnectionStatus> {
  try {
    const connection = await getConnection(ctx, connectionName, useSourceControl);
    return deriveConnectionStatus(connection);
  } catch {
    return 'error';
  }
}

/**
 * Update connection parameters (non-secret values only)
 */
export async function updateConnection(
  ctx: AppResourceContext,
  connectionName: string,
  params: UpdateConnectionParams
): Promise<ConnectionResource> {
  // Get existing connection first
  const existing = await getConnection(ctx, connectionName);

  const url = `${buildConnectionsUrl(ctx, connectionName)}?api-version=${OTTO_API_VERSION}`;

  const body = {
    properties: {
      ...existing.properties,
      ...(params.displayName && { displayName: params.displayName }),
      // When changing auth type (parameterSetName), use parameterValueSet structure.
      // Each value must be wrapped in `{ value }` (Azure managed-API schema; matches
      // createConnection and LogicAppsUX `_getRequestForCreateConnection`). Sending
      // raw scalars here causes the Otto control plane to return 500.
      // Also explicitly drop any inherited `parameterValues` so we don't send both
      // schemas in the same PUT — JSON.stringify omits `undefined`-valued keys.
      ...(params.parameterSetName && {
        parameterValueSet: {
          name: params.parameterSetName,
          values: Object.fromEntries(
            Object.entries(params.parameterValues ?? {})
              .filter(([, value]) => value !== undefined)
              .map(([key, value]) => [key, { value }])
          ),
        },
        parameterValues: undefined,
      }),
      // When only updating values (no auth type change), merge with existing parameterValues
      ...(!params.parameterSetName && params.parameterValues && {
        parameterValues: params.parameterValues,
      }),
    },
  };

  return appFetch<ConnectionResource>(url, ctx.accessToken, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

type ConsentLinkResponse = {
  value: Array<{
    link: string;
    firstPartyLoginUri?: string;
    displayName?: string;
    status?: string;
  }>;
};

type ListConsentLinksOptions = {
  connectionName: string;
  redirectUrl: string;
  objectId: string;
  tenantId: string;
};

export const listConsentLinks = async (
  ctx: AppResourceContext,
  opts: ListConsentLinksOptions,
  useSourceControl?: boolean,
): Promise<{ consentLink: string; firstPartyLoginUri?: string }> => {
  const url = `${buildConnectionsUrl(ctx, opts.connectionName, useSourceControl)}/listConsentLinks?api-version=${OTTO_API_VERSION}`;

  const requestBody = {
    parameters: [
      {
        parameterName: 'token',
        redirectUrl: opts.redirectUrl,
        objectId: opts.objectId,
        tenantId: opts.tenantId,
      },
    ],
  };

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${ctx.accessToken}`,
  };

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get consent links: ${response.status} ${errorText}`);
  }

  const data: ConsentLinkResponse = await response.json();
  const consentInfo = data.value[0];

  if (!consentInfo?.link) {
    throw new Error('No consent link returned');
  }

  const result: { consentLink: string; firstPartyLoginUri?: string } = {
    consentLink: consentInfo.link,
  };

  if (consentInfo.firstPartyLoginUri) {
    result.firstPartyLoginUri = consentInfo.firstPartyLoginUri;
  }

  return result;
};

type ConfirmConsentCodeOptions = {
  connectionName: string;
  code: string;
  objectId: string;
  tenantId: string;
};

export const confirmConsentCode = async (ctx: AppResourceContext, opts: ConfirmConsentCodeOptions, useSourceControl?: boolean): Promise<void> => {
  const url = `${buildConnectionsUrl(ctx, opts.connectionName, useSourceControl)}/confirmConsentCode?api-version=${OTTO_API_VERSION}`;

  const confirmHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${ctx.accessToken}`,
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: confirmHeaders,
    body: JSON.stringify({
      code: opts.code,
      objectId: opts.objectId,
      tenantId: opts.tenantId,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to confirm consent: ${response.status} ${errorText}`);
  }
};

// =============================================================================
// LIST MODELS (GHCP)
// =============================================================================

/**
 * Response shape from the runtime's listModels endpoint.
 * Mirrors the GitHub Copilot /models API response.
 */
export interface GhcpModel {
  id: string;
  name: string;
  version: string;
  model_picker_enabled?: boolean;
}

export interface ListModelsResponse {
  data: GhcpModel[];
}

/**
 * List models available via a GitHub Copilot agent connection.
 *
 * Calls the runtime directly at `GET /connections/{connectionName}/models`.
 * The runtime resolves the stored GitHub OAuth token (via CRP s2s broker key)
 * and proxies to `api.githubcopilot.com/models`.
 *
 * This bypasses BPM entirely — the portal talks directly to the ACA runtime
 * using the same pattern as agentUrlService (Bearer token + buildRuntimeUrl).
 */
export async function listConnectionModels(
  accessToken: string,
  connectionName: string,
  hostName: string,
): Promise<GhcpModel[]> {
  const url = `${buildRuntimeUrl(hostName)}/connections/${encodeURIComponent(connectionName)}/models?api-version=2018-11-01`;
  try {
    const response = await appFetch<ListModelsResponse>(url, accessToken);
    return response.data ?? [];
  } catch (error) {
    // The runtime /models sub-route isn't deployed everywhere yet. When it's
    // absent the call 404s — degrade to an empty list so the freeform model
    // combobox stays usable (the user can type the model name manually)
    // instead of surfacing a hard error. Other failures still propagate.
    const err = error as Error & { status?: number };
    if (err.status === 404) {
      return [];
    }
    throw error;
  }
}

/**
 * Convert ConnectionResource to AvailableConnection for UI
 */
export function toAvailableConnection(resource: ConnectionResource): AvailableConnection {
  const resourceName = extractConnectionName(resource.name);
  const result: AvailableConnection = {
    id: resource.id,
    name: resourceName,
    displayName: resource.properties.displayName ?? resourceName,
    connectorId: (resource as unknown as AppConnectionResource).properties.connectorId ?? resource.properties.api?.id,
    status: deriveConnectionStatus(resource),
    properties: {},
  };

  if (resource.properties.createdTime) {
    result.createdTime = resource.properties.createdTime;
  }
  if (resource.properties.changedTime) {
    result.lastModifiedTime = resource.properties.changedTime;
  }
  if (result.properties) {
    if (resource.properties.customParameterValues) {
      result.properties.customParameterValues = resource.properties.customParameterValues;
    }
    if (resource.properties.parameterValueSet) {
      result.properties.parameterValueSet = resource.properties.parameterValueSet;
    }
  }

  return result;
}

/**
 * Convert ConnectionResource to ConnectionReference for workflow
 */
export function toConnectionReference(resource: ConnectionResource): ConnectionReference {
  // Data-plane responses use connectorId instead of api.id — check both
  const rawApiId = resource.properties.api?.id
    ?? (resource as unknown as AppConnectionResource).properties.connectorId
    ?? '';
  // Normalize managed API IDs to canonical /managedApis/{name} format
  const apiId = isManagedApiConnector(rawApiId) ? toShortManagedApiId(rawApiId) : rawApiId;

  const result: ConnectionReference = {
    api: { id: apiId },
    connection: { id: resource.id },
    connectionName: extractConnectionName(resource.name),
  };

  if (resource.properties.connectionRuntimeUrl) {
    result.connectionRuntimeUrl = resource.properties.connectionRuntimeUrl;
  }
  if (resource.properties.connectionParameters) {
    result.connectionProperties = resource.properties.connectionParameters;
  }

  return result;
}

/**
 * Fetch connection runtime URL from get connections api.
 */
export async function fetchConnectionRuntimeUrl(
  ctx: AppResourceContext,
  connectionId: string
): Promise<string | undefined> {
  const connection = await getConnection(ctx, connectionId.split('/').slice(-1)[0] ?? '');
  return connection.properties.connectionRuntimeUrl;
}

/**
 * Backfill missing connectionRuntimeUrl values in a connections config.
 *
 * Iterates over managed API connections and fetches the runtime URL via
 * listConnectionKeys for any entry that has an empty or missing value.
 * Mutates the config in-place and returns the keys that were updated.
 */

// =============================================================================
// CONNECTOR METADATA
// =============================================================================

/**
 * Get connector metadata (including connection parameters)
 */
export interface ConnectorMetadata {
  id: string;
  name: string;
  displayName: string;
  iconUri?: string;
  brandColor?: string;
  connectionParameters?: Record<string, unknown>;
  connectionParameterSets?: Array<{
    name: string;
    uiDefinition: {
      displayName: string;
      description?: string;
    };
    parameters: Record<string, unknown>;
  }>;
  /** Alternative connection parameters (e.g., managed identity auth for legacy OAuth connectors) */
  connectionAlternativeParameters?: Record<string, unknown>;
}

export async function getConnectorMetadata(
  context: AppResourceContext,
  connectorId: string
): Promise<ConnectorMetadata> {
  const url = buildConnectorMetadataUrl(context.projectName, connectorId);

  const response = await appFetch<{
    id: string;
    name: string;
    properties: {
      generalInformation?: {
        displayName: string;
        iconUrl?: string;
        description?: string;
      };
      metadata?: {
        brandColor?: string;
      };
      connectionParameters?: Record<string, unknown>;
      connectionParameterSets?: {
        values: Array<{
          name: string;
          uiDefinition: {
            displayName: string;
            description?: string;
          };
          parameters: Record<string, unknown>;
        }>;
      };
      connectionAlternativeParameters?: Record<string, unknown>;
    };
  }>(url, context.accessToken);

  const result: ConnectorMetadata = {
    id: response.id,
    name: response.name,
    displayName: response.properties.generalInformation?.displayName ?? response.name,
  };

  if (response.properties.generalInformation?.iconUrl) {
    result.iconUri = response.properties.generalInformation.iconUrl;
  }
  if (response.properties.metadata?.brandColor) {
    result.brandColor = response.properties.metadata.brandColor;
  }
  if (response.properties.connectionParameters) {
    result.connectionParameters = response.properties.connectionParameters;
  }
  if (response.properties.connectionParameterSets?.values) {
    result.connectionParameterSets = response.properties.connectionParameterSets.values;
  }
  if (response.properties.connectionAlternativeParameters) {
    result.connectionAlternativeParameters = response.properties.connectionAlternativeParameters;
  }

  return result;
}

// =============================================================================
// UNIQUE CONNECTION NAMING
// =============================================================================

/**
 * Gets a unique connection name, checking both locally and against Azure API Hub.
 *
 * @param ctx - Azure resource context
 * @param connectorId - The connector's resource ID
 * @param connectionNames - Array of known connection names (local check)
 * @param connectorName - The connector's display name (used as prefix)
 * @returns Promise resolving to a unique connection name
 */
export async function getUniqueConnectionName(
  ctx: AppResourceContext,
  connectorId: string,
  connectionNames: string[],
  connectorName: string
): Promise<string> {
  // Clean the connector name for use as base
  const baseName = connectorName
    .replace(/[^a-zA-Z0-9-]/g, '')
    .substring(0, 20) || 'connection';

  // First, get a locally unique name
  const { name: connectionName, index } = getUniqueName(connectionNames, baseName);

  // For App resources, also verify against Azure API Hub
  if (isAppUrl(connectorId)) {
    return getUniqueConnectionNameInApp(ctx, baseName, connectionName, index, connectionNames);
  }

  return connectionName;
}

/**
 * Maximum number of attempts to find a unique connection name.
 * Prevents infinite recursion in case of API failures.
 */
const MAX_UNIQUE_NAME_ATTEMPTS = 100;

/**
 * Recursively checks API Hub until a truly unique name is found.
 * 
 * Uses the pattern from LogicAppsUX where:
 * - 404 = unique (connection doesn't exist, name is available)
 * - 200 = exists (connection exists, try next name)
 */
async function getUniqueConnectionNameInApp(
  ctx: AppResourceContext,
  baseName: string,
  connectionName: string,
  index: number,
  connectionNames: string[] = []
): Promise<string> {
  // Prevent infinite recursion - if we've tried too many times, use timestamp fallback
  if (index >= MAX_UNIQUE_NAME_ATTEMPTS) {
    const timestamp = Date.now();
    console.warn(`[connectionApi] Exceeded max attempts (${MAX_UNIQUE_NAME_ATTEMPTS}) for unique name, using timestamp fallback`);
    return `${baseName}-${timestamp}`;
  }

  // Skip API call if we already know this name exists locally
  const normalizedNames = connectionNames.map(n => n.toLowerCase());
  if (!normalizedNames.includes(connectionName.toLowerCase())) {
    // Check if the name is unique in Azure API Hub
    const isUnique = await isConnectionNameUnique(ctx, connectionName);
    if (isUnique) {
      // 404 = name is available, use it!
      return connectionName;
    }
  }

  // Name is taken (either locally or in Azure), try the next suffix
  const nextIndex = index + 1;
  const nextName = `${baseName}-${nextIndex}`;
  return getUniqueConnectionNameInApp(ctx, baseName, nextName, nextIndex, connectionNames);
}
